import { useQueryClient } from "@tanstack/react-query";

import type { ActingAsProfile, Profile } from "@tilt/admin-sdk";

import type { RoleProfile } from "./roles";

/**
 * Single source of truth for "what role view should this component
 * render?" - reads the ``ActingAsProfile`` cached by
 * ``PartnerScopeProvider`` under ``["profile", "acting-as"]``.
 *
 * When an impersonation session is active the cached response holds
 * the TARGET's identity + role grants; otherwise it's the actor's.
 * Either way, role gates + scope-aware rendering read from this hook.
 *
 * Deploy-ordering fallback: if ``/profile/acting-as`` isn't cached
 * (endpoint unavailable during a deploy, or the provider hasn't
 * fetched yet), fall back to ``["profile"]``. Callers get the
 * actor's profile as a ``RoleProfile`` - impersonation visually
 * won't swap, but every role-aware predicate keeps working.
 *
 * Uses ``getQueryData`` (no observer) so a consuming component
 * doesn't double-subscribe - PartnerScopeProvider already owns the
 * fetch. Components that need to re-render on profile changes can
 * stack their own ``useQuery`` with the same key; the provider-side
 * 60s stale time keeps that cheap.
 */
export function useRoleProfile(): RoleProfile | undefined {
  const qc = useQueryClient();
  const actingAs = qc.getQueryData<ActingAsProfile>(["profile", "acting-as"]);
  if (actingAs) return actingAs;
  return qc.getQueryData<Profile>(["profile"]);
}
